import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase, supabaseUrl, supabaseKey } from '../lib/supabase';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

/**
 * OAuth 2.1 consent screen.
 *
 * Supabase's OAuth server redirects here as
 *   /oauth/consent?authorization_id=<id>
 * whenever a third-party client (an MCP client such as Claude, for example)
 * asks for access to a TraqPack account. This page identifies the client,
 * shows the user what is being requested, and records their decision.
 *
 * The account itself is never shared: approving mints an access token scoped
 * to the signed-in user, so RLS restricts the client to exactly the devices
 * that user could already see.
 */

// Human wording for the scopes Supabase's OAuth server supports.
const SCOPE_LABELS: Record<string, string> = {
  openid: 'Confirm who you are',
  email: 'See your email address',
  profile: 'See your basic profile details',
  phone: 'See your phone number',
  offline_access: 'Stay connected without asking you to sign in again',
};

interface AuthorizationDetails {
  authorization_id: string;
  redirect_uri?: string;
  client: { id: string; name: string };
  user: { id: string; email: string };
  scope?: string;
}

function OAuthConsent() {
  const { isAuthenticated, isLoading: authLoading, signIn } = useSupabaseAuth();

  const authorizationId = new URLSearchParams(window.location.search).get('authorization_id');

  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inline sign-in, so the authorization_id survives instead of being lost to a redirect.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  // Supabase lets an authorization be READ ONLY ONCE: the first GET succeeds and
  // every later one returns 400 "authorization request cannot be processed".
  // React StrictMode double-invokes effects in development, so without this
  // guard the component burns the authorization on its own second call and the
  // user sees an error for a request that was actually fine.
  const fetchedFor = useRef<string | null>(null);

  const authHeaders = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return null;
    return {
      apikey: supabaseKey,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!authorizationId) {
        setError('This link is missing its authorization reference. Start the connection again from the app you were using.');
        setLoading(false);
        return;
      }
      // Wait for the auth context to settle; treating "not yet known" as
      // "signed out" would flash the wrong screen on every load.
      if (authLoading) return;
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      // Claim this authorization BEFORE any await. Both StrictMode invocations
      // run synchronously up to the first suspension point, so a guard placed
      // after one would let them both through.
      if (fetchedFor.current === authorizationId) return;
      fetchedFor.current = authorizationId;

      const headers = await authHeaders();
      if (!headers) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${supabaseUrl}/auth/v1/oauth/authorizations/${authorizationId}`, { headers });
        const body = await res.json().catch(() => null);

        // Remembered consent: if this user already approved this client, the
        // details call approves the new authorization itself and hands back the
        // redirect instead of a consent payload. Follow it — re-prompting for a
        // grant the user already gave would just be a dead end, since the
        // authorization has already been consumed at this point.
        if (res.ok && body?.redirect_url) {
          window.location.href = body.redirect_url;
          return;
        }

        // A stale, consumed or expired authorization comes back as 404 or as a
        // 400 validation_failed, so treat "no usable payload" as one case.
        if (!res.ok || !body?.client || !body?.user) {
          setError(
            res.status === 404 || res.status === 400
              ? 'This request has expired or was already handled. Start the connection again from the app you were using.'
              : 'Could not load this request. Please try again.',
          );
        } else {
          setDetails(body);
        }
      } catch {
        setError('Could not reach TraqPack. Check your connection and try again.');
      }
      setLoading(false);
    };
    load();
  }, [authorizationId, isAuthenticated, authLoading, authHeaders]);

  const decide = async (action: 'approve' | 'deny') => {
    setSubmitting(action);
    setError(null);
    const headers = await authHeaders();
    if (!headers) {
      setError('Your session expired. Please sign in again.');
      setSubmitting(null);
      return;
    }
    try {
      const res = await fetch(
        `${supabaseUrl}/auth/v1/oauth/authorizations/${authorizationId}/consent`,
        { method: 'POST', headers, body: JSON.stringify({ action }) },
      );
      const body = await res.json();
      if (!res.ok || !body.redirect_url) {
        setError(body?.msg || 'Could not complete this request. Please try again.');
        setSubmitting(null);
        return;
      }
      // Hand control back to the client that started the flow.
      window.location.href = body.redirect_url;
    } catch {
      setError('Could not reach TraqPack. Check your connection and try again.');
      setSubmitting(null);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setError(null);
    const result = await signIn(email, password);
    if (!result.success) setError(result.error || 'Could not sign in. Check your email and password.');
    setSigningIn(false);
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8">{children}</div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="text-center py-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black mx-auto" />
        <p className="mt-4 text-gray-600">Loading request…</p>
      </div>,
    );
  }

  if (error && !details) {
    return shell(
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
        <p className="mt-3 text-gray-600">{error}</p>
      </div>,
    );
  }

  // Not signed in yet — authenticate without losing the pending authorization.
  if (!isAuthenticated) {
    return shell(
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Sign in to continue</h1>
        <p className="mt-2 text-gray-600">
          An application is asking to connect to your TraqPack account. Sign in to review the request.
        </p>
        <form onSubmit={handleSignIn} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <input
              id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
            <input
              id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit" disabled={signingIn}
            className="w-full rounded-lg bg-black text-white py-2.5 font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            {signingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>,
    );
  }

  if (!details) return shell(<p className="text-gray-600">Nothing to review.</p>);

  const scopes = (details.scope ?? '').split(' ').filter(Boolean);

  return shell(
    <div>
      <h1 className="text-xl font-semibold text-gray-900">
        Connect <span className="font-bold">{details.client.name}</span>?
      </h1>
      <p className="mt-2 text-gray-600">
        It is asking to access your TraqPack account as{' '}
        <span className="font-medium text-gray-900">{details.user.email}</span>.
      </p>

      <div className="mt-6 rounded-lg bg-gray-50 border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-900">This will let it:</p>
        <ul className="mt-2 space-y-1.5">
          <li className="text-sm text-gray-700 flex gap-2">
            <span aria-hidden="true">•</span>
            <span>See your trackers, their locations and their history</span>
          </li>
          {scopes.map((s) => (
            <li key={s} className="text-sm text-gray-700 flex gap-2">
              <span aria-hidden="true">•</span>
              <span>{SCOPE_LABELS[s] ?? s}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-gray-500">
          It only ever sees the devices on your own account, and never your password.
        </p>
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      <div className="mt-6 flex gap-3">
        <button
          onClick={() => decide('deny')} disabled={submitting !== null}
          className="flex-1 rounded-lg border border-gray-300 py-2.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {submitting === 'deny' ? 'Cancelling…' : 'Cancel'}
        </button>
        <button
          onClick={() => decide('approve')} disabled={submitting !== null}
          className="flex-1 rounded-lg bg-black text-white py-2.5 font-medium hover:bg-gray-800 disabled:opacity-50"
        >
          {submitting === 'approve' ? 'Connecting…' : 'Allow'}
        </button>
      </div>

      {details.redirect_uri && (
        <p className="mt-4 text-xs text-gray-400 break-all">Redirects to {details.redirect_uri}</p>
      )}
    </div>,
  );
}

export default OAuthConsent;
